import { Layout } from "@/components/layout/Layout";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { FinalCTA } from "@/components/home/FinalCTA";
import { WhyChooseUs } from "@/components/home/WhyChooseUs";
import { SEO } from "@/components/SEO";
import { localBusinessSchema } from "@/lib/structuredData";
import { Target, Shield, Eye, Clock, Users } from "lucide-react";
import saicaBadge from "@/assets/saica-badge.png?format=webp&w=200";
import saicaBadgeFallback from "@/assets/saica-badge.png";

const values = [
  {
    icon: Target,
    title: "Accuracy",
    description: "Every number matters. We maintain meticulous attention to detail in all our work.",
  },
  {
    icon: Shield,
    title: "Confidentiality",
    description: "Your financial information is treated with the utmost discretion and security.",
  },
  {
    icon: Clock,
    title: "Consistency",
    description: "Reliable, on-time delivery every month. You can count on us.",
  },
  {
    icon: Eye,
    title: "Transparency",
    description: "Clear pricing, clear communication, no surprises.",
  },
  {
    icon: Users,
    title: "Responsiveness",
    description: "Quick replies and genuine care for every client we serve.",
  },
];


const About = () => {
  return (
    <Layout>
      <SEO 
        title="About Our SAICA Registered Bookkeeping Team | South Africa"
        description="SAICA registered bookkeeping services built on trust. We help SMEs, startups, and growing businesses across South Africa with professional financial administration."
        canonical="/about"
        structuredData={localBusinessSchema}
      />
      {/* Hero */}
      <section className="py-16 lg:py-24 section-accent">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-6">
              About Us
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Built on trust. Built for compliance. We're your dedicated partners in 
              keeping South African businesses financially healthy.
            </p>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 lg:py-28 section-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-block text-sm font-medium text-primary mb-3 tracking-wide uppercase">
                  Our Mission
                </span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-6">
                  Built on Trust, Focused on Compliance
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  At Bookkeeping Services South Africa, we believe that every business deserves 
                  professional, reliable financial administration. As a <strong className="text-foreground">SAICA registered</strong> practice, 
                  our mission is to provide peace of mind through accurate bookkeeping, timely compliance, 
                  and clear communication.
                </p>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  We take the complexity out of SARS requirements, payroll obligations, and 
                  financial reporting — so you can focus on what you do best: growing your business.
                </p>
                <div className="flex items-center gap-4 bg-accent rounded-xl p-4">
                  <picture>
                    <source srcSet={saicaBadge} type="image/webp" />
                    <img 
                      src={saicaBadgeFallback} 
                      alt="SAICA Registered" 
                      className="h-24"
                      width={200}
                      height={283}
                      loading="lazy"
                      decoding="async"
                    />
                  </picture>
                  <span className="text-sm font-medium text-foreground">
                    Proud member of the South African Institute of Chartered Accountants
                  </span>
                </div>
              </div>
              <div className="bg-accent rounded-2xl p-8">
                <h3 className="font-heading text-xl font-semibold text-foreground mb-4">
                  Who We Help
                </h3>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full bg-primary"></span>
                    Small and Medium Enterprises (SMEs)
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full bg-primary"></span>
                    Startups and New Businesses
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full bg-primary"></span>
                    Growing Companies
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full bg-primary"></span>
                    Established Businesses Seeking Efficiency
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full bg-primary"></span>
                    Businesses with Compliance Backlogs
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 lg:py-28 section-light">
        <div className="container mx-auto px-4">
          <SectionHeader
            label="Our Values"
            title="What We Stand For"
            description="These principles guide everything we do for our clients."
          />

          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6 max-w-6xl mx-auto">
            {values.map((value) => (
              <div key={value.title} className="text-center p-6">
                <div className="icon-container mx-auto mb-4">
                  <value.icon className="w-6 h-6" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
                  {value.title}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>


      <WhyChooseUs />
      <FinalCTA />
    </Layout>
  );
};

export default About;
